export interface PreparedUpload {
  original: Blob;
  view: Blob;
  width: number;
  height: number;
}

const supportedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);

export async function prepareUploadImage(file: File): Promise<PreparedUpload> {
  if (!supportedTypes.has(file.type)) {
    throw new Error("Use a JPEG, PNG, or WebP image.");
  }

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    throw new Error("Could not verify this image.");
  }
  if (bitmap.width < 180 || bitmap.height < 180) {
    bitmap.close();
    throw new Error("Image must be at least 180px wide and tall.");
  }

  try {
    const original = await renderJpeg(bitmap, 2200, 0.88);
    const view = await renderJpeg(bitmap, 900, 0.78);
    return {
      original,
      view,
      width: bitmap.width,
      height: bitmap.height
    };
  } finally {
    bitmap.close();
  }
}

async function renderJpeg(bitmap: ImageBitmap, maxSide: number, quality: number): Promise<Blob> {
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image compression is not supported in this browser.");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error("Image compression failed.");
  return blob;
}

